import api from './api';

/**
 * 共用端末(キオスク)打刻のAPI。
 *
 * ## 2つの経路が混ざらないように分けてある
 *
 * - `kioskDevice*` … **端末**として叩く。認証は端末トークン Cookie(ログイン不要)
 * - `kioskAdmin*`  … **管理者**として叩く。通常のログイン + 管理者権限
 *
 * 前者はキオスク画面からのみ、後者は管理画面からのみ使う。
 */

export type PunchAction = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
export type PunchState = 'not_clocked_in' | 'working' | 'on_break';

export const PUNCH_LABELS: Record<PunchAction, string> = {
  clock_in: '出勤',
  clock_out: '退勤',
  break_start: '休憩開始',
  break_end: '休憩終了',
};

/** 画面に出す打刻時刻。未打刻は null */
export interface PunchTimes {
  clockIn: string | null;
  clockOut: string | null;
  breakStart: string | null;
  breakEnd: string | null;
}

export interface KioskIdentifyResult {
  user: { id: number; name: string; employeeNumber: string | null };
  state: PunchState;
  available: PunchAction[];
  times: PunchTimes;
  /** 会社設定。true なら休憩打刻を使わない運用 */
  autoBreakDeduction: boolean;
}

export interface KioskPunchResult {
  user: { name: string };
  action: PunchAction;
  time: string;
  times: PunchTimes;
  autoBreakDeduction: boolean;
}

// ---------------------------------------------------------------- 端末として

/** ペアリング。成功すると端末トークンが Cookie に入る */
export async function pairDevice(code: string): Promise<{ deviceId: number }> {
  const res = await api.post('/kiosk/pair', { code });
  return res.data;
}

/** QRを読んだ直後。名前と「押せる動作」だけが返る(時刻は返らない) */
export async function identify(qrToken: string): Promise<KioskIdentifyResult> {
  const res = await api.post('/kiosk/identify', { qrToken });
  return res.data;
}

/** 打刻。サーバ側で QR をもう一度検証する(画面の状態を信用しない) */
export async function punch(qrToken: string, action: PunchAction): Promise<KioskPunchResult> {
  const res = await api.post('/kiosk/punch', { qrToken, action });
  return res.data;
}

/** この端末が登録済みかの確認。未登録なら 401 が返る */
export async function heartbeat(): Promise<{ ok: boolean; deviceName: string }> {
  const res = await api.post('/kiosk/heartbeat');
  return res.data;
}

// ---------------------------------------------------------------- 管理者として

export interface KioskDevice {
  id: number;
  name: string;
  created_at: string;
  last_seen_at: string | null;
  revoked_at: string | null;
  paired: boolean;
  pairing_pending: boolean;
  pairing_expires_at: string | null;
}

export interface PunchRoutes {
  /** 共用端末からの打刻 */
  kioskEnabled: boolean;
  /** 本人の画面（ダッシュボード）からの打刻 */
  selfPunchEnabled: boolean;
}

export async function listDevices(
  includeRevoked = false
): Promise<PunchRoutes & { devices: KioskDevice[]; revokedCount: number }> {
  const res = await api.get('/kiosk-devices', {
    params: includeRevoked ? { includeRevoked: '1' } : undefined,
  });
  return res.data;
}

/**
 * 打刻経路の有効/無効。**両方を無効にはできない**（誰も打刻できなくなる）。
 * サーバ側でも弾くが、画面でも押せないようにしている。
 */
export async function setPunchRoutes(
  routes: Partial<{ enabled: boolean; selfPunchEnabled: boolean }>
): Promise<PunchRoutes> {
  const res = await api.put('/kiosk-devices/enabled', routes);
  return res.data;
}

/** 自分のダッシュボードに打刻用QRを常時出すか */
export async function setPunchQrDisplay(show: boolean): Promise<{ showPunchQrOnDashboard: boolean }> {
  const res = await api.put('/users/me/punch-qr-display', { show });
  return res.data;
}

/** 端末を登録。**ペアリングコードの生値が返るのはこの1回だけ** */
export async function createDevice(name: string): Promise<{ device: KioskDevice; pairingCode: string }> {
  const res = await api.post('/kiosk-devices', { name });
  return res.data;
}

/** コードの再発行。**その端末の既存トークンは無効になる** */
export async function reissuePairingCode(id: number): Promise<{ device: KioskDevice; pairingCode: string }> {
  const res = await api.post(`/kiosk-devices/${id}/pairing-code`);
  return res.data;
}

/** 端末の失効。行は消さない(監査ログから辿れる必要があるため) */
export async function revokeDevice(id: number): Promise<void> {
  await api.delete(`/kiosk-devices/${id}`);
}

// ---------------------------------------------------------------- 打刻用QR

export interface PunchQrEntry {
  userId: number;
  name: string;
  employeeNumber: string | null;
  /** 未発行なら null */
  qrToken: string | null;
  issuedAt: string | null;
}

/**
 * 発行済みQRの一覧(管理者)。**再印刷のために使う。**
 * これがあるので、印刷し直すのに再発行する必要がない。
 */
export async function listPunchQrs(): Promise<PunchQrEntry[]> {
  const res = await api.get('/users/punch-qr');
  return res.data.users;
}

/**
 * 従業員のQRを発行・再発行する(管理者)。
 *
 * ⚠️ **再発行は破壊的**。その人に配布済みのQR(印刷したカード・スマホ表示)が
 * すべて即座に使えなくなる。印刷し直したいだけなら listPunchQrs の値を刷ればよい。
 */
export async function issuePunchQr(userId: number): Promise<{
  user: { id: number; name: string; employeeNumber: string | null };
  qrToken: string;
}> {
  const res = await api.post(`/users/${userId}/punch-qr`);
  return res.data;
}

/**
 * 自分のQRを取得する(スマホ画面に表示する用)。
 *
 * **呼んでも値は変わらない**(未発行のときだけ発行される)。
 * ここで毎回作り直すと、管理者が印刷して配ったQRがその瞬間に無効になる。
 */
export async function getMyPunchQr(): Promise<{ qrToken: string; showOnDashboard: boolean }> {
  const res = await api.get('/users/me/punch-qr');
  return res.data;
}
